import { store } from "@redux";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { authApi, merchantApi } from "@api";
import { enumExternalMethod, PATH_WELCOME } from "@configs";
import {
    IAuth,
    IFBLogin,
    IFBSignup,
    IGGLogin,
    IGGSignup,
    IInfo,
    ILineLogin,
    ILinePhone,
    ILineSignup,
    ILogin,
    ITerms,
} from "@interfaces";
import { RootState } from ".";

export const login = createAsyncThunk<IAuth, ILogin>(
    "auth/login",
    async (values: ILogin, { rejectWithValue }) => {
        try {
            const res = await authApi.login(values);
            return res.data as IAuth;
        } catch (err: any) {
            return rejectWithValue(err);
        }
    }
);

export const lineLogin = createAsyncThunk<IAuth, ILineLogin>(
    "auth/lineLogin",
    async (values, { rejectWithValue }) => {
        try {
            const res = await authApi.lineLogin(values);
            const merchantId = store.getState().merchant._id;
            return { ...res.data, merchantId } as IAuth;
        } catch (err: any) {
            return rejectWithValue(err);
        }
    }
);

export const lineSignup = createAsyncThunk<IAuth, ILineSignup>(
    "auth/lineSignup",
    async (values, { rejectWithValue }) => {
        try {
            const res = await authApi.lineSignup(values);
            const merchantId = store.getState().merchant._id;
            return { ...res.data, merchantId } as IAuth;
        } catch (err: any) {
            return rejectWithValue(err);
        }
    }
);

export const fbLogin = createAsyncThunk<IAuth, IFBLogin>(
    "auth/fbLogin",
    async (values, { rejectWithValue }) => {
        try {
            const res = await authApi.fbLogin(values);
            const merchantId = store.getState().merchant._id;
            return { ...res.data, merchantId } as IAuth;
        } catch (err: any) {
            return rejectWithValue(err);
        }
    }
);

export const fbSignup = createAsyncThunk<IAuth, IFBSignup>(
    "auth/fbSignup",
    async (values, { rejectWithValue }) => {
        try {
            const res = await authApi.fbSignup(values);
            const merchantId = store.getState().merchant._id;
            return { ...res.data, merchantId } as IAuth;
        } catch (err: any) {
            return rejectWithValue(err);
        }
    }
);

export const ggLogin = createAsyncThunk<IAuth, IGGLogin>(
    "auth/google-signin",
    async (values, { rejectWithValue }) => {
        try {
            const res = await authApi.ggLogin(values);
            const merchantId = store.getState().merchant._id;
            return { ...res.data, merchantId } as IAuth;
        } catch (err: any) {
            return rejectWithValue(err);
        }
    }
);

export const ggSignup = createAsyncThunk<IAuth, IGGSignup>(
    "auth/google-signup",
    async (values, { rejectWithValue }) => {
        try {
            const res = await authApi.ggSignup(values);
            const merchantId = store.getState().merchant._id;
            return { ...res.data, merchantId } as IAuth;
        } catch (err: any) {
            return rejectWithValue(err);
        }
    }
);

export const getInfo = createAsyncThunk("auth/userInfo", async (values, { rejectWithValue }) => {
    try {
        const res = await authApi.getInfo();
        return res.data as IInfo;
    } catch (err: any) {
        return rejectWithValue(err);
    }
});
export const getTerms = createAsyncThunk("auth/terms", async (values, { rejectWithValue }) => {
    try {
        const res = await merchantApi.getMerchantTermCondition();
        return res.data.result as ITerms;
    } catch (err: any) {
        return rejectWithValue(err);
    }
});

export const fetchUser = createAsyncThunk("user/fetchUser", async (userId, { rejectWithValue }) => {
    try {
        const response = await authApi.getInfo();
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error);
    }
});

const preformFields = JSON.parse(sessionStorage.getItem("preformFields") || "{}");

interface ILine {
    code: string | null;
    state: string;
}
interface IState {
    auth: IAuth | null;
    isRemember: boolean;
    isLoading: boolean;
    error: string;
    tokenInfoAuth: string;
    userInfo: IInfo | null;
    line?: ILine | null;
    externalLogin: enumExternalMethod | null;
    merchantId: string;
    isSignUp: boolean;
    detailUser: boolean;
    showConfirm: boolean;
    terms: ITerms | null;
    lineToken: ILinePhone | null;
    merchant_prefix: string;
    preformFields: any;
}

interface ISetExternalLogin {
    line?: ILine;
    type: enumExternalMethod;
    isSignUp?: boolean;
}

const initialState: IState = {
    auth: null,
    isRemember: false,
    isLoading: false,
    error: "",
    tokenInfoAuth: "",
    userInfo: null,
    externalLogin: null,
    merchantId: "",
    merchant_prefix: "",
    isSignUp: false,
    detailUser: false,
    showConfirm: false,
    terms: null,
    lineToken: null,
    preformFields: preformFields ? preformFields : {},
};

const authSlice = createSlice({
    name: "auth",
    initialState: initialState,
    reducers: {
        logout: (state) => {
            // return initialState;
            state.auth = null;
            localStorage.clear();
            sessionStorage.clear();
            console.log("replacing in authSlice > logout");
            window.location.replace(PATH_WELCOME);
        },
        setAuth: (state, action: PayloadAction<IAuth | null>) => {
            if (action.payload) state.auth = { ...state.auth, ...action.payload };
            else state.auth = action.payload;
        },

        setInfo: (state, action) => {
            state.userInfo = { ...state.userInfo, ...action.payload };
        },

        setRemember: (state, action: PayloadAction<boolean>) => {
            state.isRemember = action.payload;
        },
        setExternalLogin: (state, action: PayloadAction<ISetExternalLogin>) => {
            state.externalLogin = action.payload.type;
            state.line = action.payload.line;
            state.isSignUp = action.payload.isSignUp || false;
            state.isLoading = true;
        },

        setMerchantID: (state, action: PayloadAction<string>) => {
            state.merchantId = action.payload;
        },
        setMerchantPrefix: (state, action: PayloadAction<string>) => {
            state.merchant_prefix = action.payload;
        },
        setDetailUser: (state) => {
            state.detailUser = !state.detailUser;
        },
        setToggleConfirm: (state) => {
            state.showConfirm = !state.showConfirm;
        },
        resetConfirm: (state) => {
            state.showConfirm = initialState.showConfirm;
        },
        resetDetailUser: (state) => {
            state.detailUser = false;
        },
        resetAuth: (state) => {
            state.auth = null;
            state.isSignUp = true;
            state.userInfo = null;
        },
        setLineToken: (state, action: PayloadAction<ILinePhone>) => {
            state.lineToken = action.payload;
        },
        resetAuthState: (state) => {
            state = initialState;
        },
        resetLineToken: (state) => {
            state.lineToken = null;
        },
        setPreformFields: (state, action: PayloadAction<any>) => {
            state.preformFields = action.payload;
            sessionStorage.setItem("preformFields", JSON.stringify(action.payload));
        },
    },
    extraReducers: (builder) => {
        builder.addCase(login.fulfilled, (state, action: { payload: IAuth }) => {
            if (action.payload.accessToken) {
                state.auth = action.payload;
            }
            if (!state.isRemember) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                state.auth!.refreshToken = null;
            }
            state.isLoading = false;
        });

        builder.addCase(login.pending, (state) => {
            state.isLoading = true;
        });

        builder.addCase(login.rejected, (state) => {
            state.auth = null;
            state.isLoading = false;
        });

        builder.addCase(getInfo.fulfilled, (state, action: { payload: IInfo }) => {
            state.userInfo = action.payload;
        });

        builder.addCase(getInfo.rejected, (state) => {
            state.userInfo = null;
        });

        builder.addCase(lineLogin.fulfilled, (state, action: { payload: IAuth }) => {
            if (action.payload.accessToken) {
                state.auth = action.payload;
            }
        });

        builder.addCase(lineLogin.rejected, (state) => {
            state.auth = null;
            state.externalLogin = null;
            state.line = null;
            state.isLoading = false;
        });

        builder.addCase(lineSignup.fulfilled, (state, action: { payload: IAuth }) => {
            if (action.payload.accessToken) {
                state.auth = action.payload;
            }
        });

        builder.addCase(lineSignup.rejected, (state) => {
            state.auth = null;
            state.externalLogin = null;
            state.line = null;
            state.isLoading = false;
        });

        builder.addCase(fbLogin.fulfilled, (state, action: { payload: IAuth }) => {
            if (action.payload.accessToken) {
                state.auth = action.payload;
            }
        });

        builder.addCase(fbLogin.rejected, (state) => {
            state.auth = null;
            state.externalLogin = null;
            state.isLoading = false;
        });

        builder.addCase(fbSignup.fulfilled, (state, action: { payload: IAuth }) => {
            if (action.payload.accessToken) {
                state.auth = action.payload;
            }
        });

        builder.addCase(fbSignup.rejected, (state) => {
            state.auth = null;
            state.externalLogin = null;
            state.isLoading = false;
        });

        builder.addCase(ggLogin.fulfilled, (state, action: { payload: IAuth }) => {
            if (action.payload.accessToken) {
                state.auth = action.payload;
            }
        });

        builder.addCase(ggLogin.rejected, (state) => {
            state.auth = null;
            state.externalLogin = null;
            state.isLoading = false;
        });

        builder.addCase(ggSignup.fulfilled, (state, action: { payload: IAuth }) => {
            if (action.payload.accessToken) {
                state.auth = action.payload;
            }
        });

        builder.addCase(ggSignup.rejected, (state) => {
            state.auth = null;
            state.externalLogin = null;
            state.isLoading = false;
        });
        builder.addCase(getTerms.fulfilled, (state, action: { payload: ITerms }) => {
            state.terms = action.payload;
        });

        builder.addCase(getTerms.rejected, (state) => {
            state.terms = null;
        });

        builder
            .addCase(fetchUser.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchUser.fulfilled, (state, action) => {
                state.isLoading = false;
                state.userInfo = action.payload;
            })
            .addCase(fetchUser.rejected, (state) => {
                state.isLoading = false;
            });
    },
});

export const selectAuth = (state: RootState) => state.auth;

export const {
    logout,
    setAuth,
    setInfo,
    setRemember,
    setExternalLogin,
    setMerchantID,
    setMerchantPrefix,
    setDetailUser,
    setToggleConfirm,
    resetConfirm,
    resetDetailUser,
    resetAuth,
    resetAuthState,
    setLineToken,
    resetLineToken,
    setPreformFields,
} = authSlice.actions;

export default authSlice.reducer;
